<template>
    <v-app>
        <div class="demo-wrapper">
            <backdrop-demo />

            <transition name="fade">
                <div>
                    <div
                        v-if="!submitted && !thank_you_shown"
                        class="form-container text-center"
                    >
                        <v-row>
                            <v-col
                                cols="5"
                                class="side-by-side-image-wrapper"
                                v-if="$vuetify.breakpoint.lgAndUp"
                            >
                                <img
                                    src="/app/guest-checkin-images/wine-glass-pour.jpg"
                                    class="side-by-side-image"
                                    alt=""
                                />
                            </v-col>

                            <v-col
                                cols="12"
                                lg="7"
                            >
                                <branding-header />

                                <offer-demo class="mt-4 mb-7" />

                                <form-demo
                                    ref="form"
                                    :submit_text="form_submit_text"
                                    v-model="form_data"
                                    @submit="submitForm"
                                >
                                    <template #email>
                                        <v-text-field
                                            v-model="form_data.email"
                                            label="Email Address"
                                            type="email"
                                            autocomplete="off"
                                            outlined
                                            prepend-inner-icon="mdi-email"
                                            validate-on-blur
                                            class="ma-0"
                                            :class="[!form_data.email && 'mb-10']"
                                            required
                                            :rules="[rules.required, rules.email_verification]"
                                            hide-details
                                        />

                                        <transition name="fade">
                                            <div v-if="form_data.email">
                                                <v-checkbox
                                                    v-model="form_data.email_consent"
                                                    class="mt-3 mb-8"
                                                    color="primary"
                                                    hide-details
                                                >
                                                    <template #label>
                                                        <span class="disclaimer text-left">
                                                            I give Barrel Creek Vineyards permission to email me with
                                                            info and
                                                            offers. Use is subject to <a
                                                                href="#"
                                                                @click.prevent="$refs.placeholderDialog.show('Terms & Conditions')"
                                                            >Terms</a>
                                                            with an arbitration clause and a <a
                                                                href="#"
                                                                @click.prevent="$refs.placeholderDialog.show('Privacy Policy')"
                                                            >Privacy Policy</a>.
                                                        </span>
                                                    </template>
                                                </v-checkbox>
                                            </div>
                                        </transition>
                                    </template>

                                    <template #phone>
                                        <v-text-field
                                            v-model="form_data.phone"
                                            hide-details
                                            label="Mobile Phone Number (optional)"
                                            type="tel"
                                            autocomplete="off"
                                            outlined
                                            prepend-inner-icon="mdi-cellphone"
                                            validate-on-blur
                                            class="mt-2 pt-0"
                                            @input="onInputPhone"
                                            :rules="[rules.optional_phone_verification]"
                                        />

                                        <transition name="fade">
                                            <div v-if="form_data.phone">
                                                <v-checkbox
                                                    v-model="form_data.phone_consent"
                                                    class="mt-3"
                                                    color="primary"
                                                    hide-details
                                                >
                                                    <template #label>
                                                        <span class="disclaimer text-left">
                                                            By submitting this form, I give Barrel Creek Vineyards
                                                            permission to
                                                            send
                                                            email and text messages
                                                            with info, promotions, and offers to the number I have
                                                            provided,
                                                            including
                                                            use of an automated system.
                                                            Consent is not a condition of purchase. Texts are recorded.
                                                            Message
                                                            and/or
                                                            data rates may apply.
                                                            Message frequency varies. Text STOP to opt out or HELP for
                                                            help at
                                                            any
                                                            time.
                                                            Use is subject to <a
                                                                href="#"
                                                                @click.prevent="$refs.placeholderDialog.show('Terms & Conditions')"
                                                            >Terms</a>
                                                            with an arbitration clause and a <a
                                                                href="#"
                                                                @click.prevent="$refs.placeholderDialog.show('Privacy Policy')"
                                                            >Privacy Policy</a>.
                                                        </span>
                                                    </template>
                                                </v-checkbox>
                                            </div>
                                        </transition>

                                        <v-dialog
                                            v-model="show_discount_consent_warning"
                                            max-width="620"
                                            attach=".demo-wrapper"
                                        >
                                            <v-card>
                                                <v-card-title class="headline">
                                                    <v-icon
                                                        left
                                                        color="primary"
                                                        class="mr-2"
                                                    >mdi-alert</v-icon>
                                                    Heads up!
                                                </v-card-title>

                                                <v-card-text>
                                                    <span>
                                                        To receive your exclusive 10% discount code, we need your
                                                        permission to text you. Please check the consent box above to
                                                        continue.
                                                    </span>
                                                </v-card-text>

                                                <v-card-actions>
                                                    <v-spacer />

                                                    <v-btn
                                                        text
                                                        @click="show_discount_consent_warning = false"
                                                    >
                                                        Go Back
                                                    </v-btn>

                                                    <v-btn
                                                        color="primary"
                                                        @click="onContinueWithoutDiscount"
                                                    >
                                                        Continue Without Discount
                                                    </v-btn>
                                                </v-card-actions>
                                            </v-card>
                                        </v-dialog>
                                    </template>
                                </form-demo>

                            </v-col>
                        </v-row>
                    </div>

                    <thank-you-demo
                        v-else-if="thank_you_shown && !double_opt_in_demo_shown"
                        :form_data="form_data"
                        has_flow_demo_after
                        @show-demo="double_opt_in_demo_shown = true"
                    >
                        <h3
                            v-if="form_data.phone && form_data.phone_consent"
                            class="text-h5 mb-4"
                        >🎉 Almost There, {{ form_data.first_name
                        }}!</h3>
                        <h3 v-else></h3>

                        <p
                            v-if="form_data.phone && form_data.phone_consent"
                            class="text-left mb-4"
                        >
                            Thanks for checking in! We're excited to send you that exclusive 10% discount code.
                            You'll receive a text message shortly asking you to confirm your opt-in.
                            Once you reply "YES", we'll immediately send your discount code!
                        </p>
                        <p v-else>
                            Welcome to Barrel Creek Vineyards, a! We're excited to have you join us today. Your
                            information has been
                            securely recorded and you're all set to enjoy your visit.

                            Our team will be notified of your arrival. Please feel free to explore our tasting room and
                            don't hesitate to
                            ask our staff about our current wine selections and upcoming events.
                        </p>

                        <p class="text-left mb-4">
                            In the meantime, feel free to explore our tasting room and ask our staff about our
                            current wine selections. Your discount code will work on purchases today or future visits!
                        </p>
                    </thank-you-demo>

                    <flow-demo
                        v-else-if="double_opt_in_demo_shown"
                        :form_data="form_data"
                        :conversation="conversation"
                        technical_notes_heading="How the Discount System Works"
                        @reset-form="backToForm"
                    >
                        <template #technical-notes>
                            <ul>
                                <li v-if="form_data.phone && form_data.phone_consent">
                                    <strong>Double Opt-In Required:</strong> Discount code is only sent AFTER
                                    user confirms opt-in by replying "YES" to our text message
                                </li>
                                <li v-if="form_data.phone && form_data.phone_consent">
                                    <strong>Time Limit:</strong> If user doesn't reply "YES" within 1 day,
                                    it's treated the same as texting "STOP" and no discount code will be sent
                                </li>
                                <li v-if="form_data.phone && form_data.phone_consent">
                                    <strong>Already Opted In?</strong> Existing subscribers won't get the double
                                    opt-in message - they'll receive the discount code immediately with a thank you
                                    message
                                </li>
                                <li v-if="form_data.email && form_data.email_consent">
                                    <strong>Email Marketing:</strong> Email consent is automatically recorded in
                                    Commerce7 for future promotions and offers
                                </li>
                                <li>
                                    <strong>Customer Management:</strong> A new customer profile will be
                                    automatically created in Commerce7 if one doesn't already exist
                                </li>
                                <li>
                                    <strong>Smart Tagging:</strong> RedChirp applies custom tags like
                                    "Discount-Checkin" to track which customers came through this promotion for future
                                    marketing
                                </li>
                            </ul>
                        </template>
                    </flow-demo>
                </div>
            </transition>

            <placeholder-dialog ref="placeholderDialog" />
        </div>
    </v-app>
</template>

<script>
import BackdropDemo from '@/components/demos/shared/backdrop.vue';
import BrandingHeader from '@/components/demos/shared/branding-header.vue';
import OfferDemo from '@/components/demos/guest-checkin/offer-demo.vue';
import FormDemo from '@/components/demos/guest-checkin/form-demo.vue';
import ThankYouDemo from '@/components/demos/guest-checkin/thank-you-demo.vue';
import FlowDemo from '@/components/demos/guest-checkin/flow-demo.vue';
import PlaceholderDialog from '@/components/demos/shared/placeholder-dialog.vue';
import Validate from '@/js/validate.js';

export default {
    components: {
        BackdropDemo,
        BrandingHeader,
        OfferDemo,
        FormDemo,
        ThankYouDemo,
        FlowDemo,
        PlaceholderDialog
    },

    data() {
        return {
            submitted: false,
            thank_you_shown: false,
            double_opt_in_demo_shown: false,
            show_discount_consent_warning: false,
            form_data: {
                first_name: '',
                last_name: '',
                birth_date: null,
                email: '',
                email_consent: false,
                phone: '',
                phone_consent: false,
            },
            rules: {
                required: value => !!value || 'This field is required',
                email_verification: value => {
                    if (!Validate.isEmailAddress(value?.trim())) {
                        return 'Please enter a valid email address';
                    }
                    return true;
                },
                optional_phone_verification: value => {
                    if (!value || value.trim() === '') {
                        return true;
                    }

                    if (!Validate.isUSPhoneNumber(value?.trim())) {
                        return 'A valid phone number is required';
                    }
                    return true;
                },
            },
        }
    },

    computed: {
        form_submit_text() {
            return this.form_data.phone && this.form_data.phone_consent ? 'Get My Discount' : 'Agree and Submit';
        },

        conversation() {
            return [
                {
                    type: 'company',
                    text: 'Reply YES to receive messages with info, promotions and offers. We may use automation to send these texts. Consent is not a condition of purchase. Texts and calls may be recorded. Msg frequency varies. Msg and/or data rates may apply.',
                    sender: 'Barrel Creek Vineyards'
                },
                {
                    type: 'customer',
                    text: 'YES',
                    sender: this.form_data.first_name
                },
                {
                    type: 'company',
                    text: 'You\'re now subscribed to receive updates from Barrel Creek Vineyards. Text STOP to opt out or HELP for help at any time.',
                    sender: 'Barrel Creek Vineyards'
                },
                {
                    type: 'company',
                    text: `🎉 Congratulations ${this.form_data.first_name}! Here's your exclusive 10% discount code: **SAVE10NOW**\n\nShow this code to any staff member or use it online. Valid for 30 days. Thanks for joining our community!`,
                    sender: 'Barrel Creek Vineyards'
                }
            ];
        }
    },

    methods: {
        onInputPhone(value) {
            const updated = { ...this.form_data, phone: this.$rc.maskPhoneNumber(value) };
            this.form_data = updated;
        },

        submitForm() {
            if (this.$refs.form?.validate()) {
                if (this.form_data.phone && !this.form_data.phone_consent) {
                    this.show_discount_consent_warning = true;
                    return;
                }

                this.scrollToTop();
                this.submitted = true;
                this.thank_you_shown = true;
            }
        },

        onContinueWithoutDiscount() {
            this.scrollToTop();
            this.submitted = true;
            this.thank_you_shown = true;
            this.show_discount_consent_warning = false;
        },

        resetForm() {
            this.form_data = {
                first_name: '',
                last_name: '',
                birth_date: null,
                email: '',
                phone: ''
            };
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },

        backToForm() {
            this.resetForm();
            this.submitted = false;
            this.scrollToTop();
            this.thank_you_shown = false;
            this.double_opt_in_demo_shown = false;
        },

        scrollToTop() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    },

    mounted() {
        document.title = "Guest Check-In | Discount";
    }
}
</script>

<style lang="scss" scoped>
.demo-wrapper {
    --brand-primary: #40728F;
    --v-primary-base: var(--brand-primary);
    --v-anchor-base: var(--brand-primary);
    --max-width: 1200px;

    position: relative;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

    .side-by-side-image-wrapper {
        position: relative;
        overflow: hidden;
    }

    .side-by-side-image {
        object-fit: cover;
        object-position: right;
        max-width: 100%;
        width: 100%;
        height: 100%;
        border-radius: 0.5rem;
        transition: all 0.3s ease-in-out;
    }

    .heading {
        font-size: 1.5rem;
        font-weight: 600;
        margin-top: 0.5rem;
        margin-bottom: 0.25rem;
        color: var(--v-primary-base);
    }

    .subheading {
        font-size: 1.2rem;
        font-weight: 500;
        margin-bottom: 1.5rem;
        color: #666666;
    }

    .subtext {
        font-size: 12px;
    }

    .form-container {
        position: relative;
        max-width: min(var(--max-width), 90%);
        margin: 2rem auto;
        padding: 2rem;
        background: rgba(255, 255, 255, 0.95);
        border-radius: 1rem;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-top: 4px solid var(--v-primary-base);
    }

    .disclaimer {
        color: rgba(0, 0, 0, 0.6);
        font-style: italic;
        font-size: 1rem; // must be same size as input labels
    }

    :deep(.v-input--selection-controls__input) {
        align-self: start;
    }


}
</style>